/* RESPONSIBLE TEAM: team-reporting */

import { Trans, useLingui } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  ChartSchema,
  ChartDefinition,
  DateRange,
  Timezone,
} from '@/features/report-snapshots/api/schema';
import { cn } from '@intercom/surge';
import { SmallUpArrowIcon } from '@intercom/surge/icons';
import { createSchemaComparator } from '@/features/report-snapshots/utils/schema-comparator';
import { SmallDownArrowIcon } from '@intercom/surge/icons';
import { formatComparisonValue } from '@/features/report-snapshots/formatters/cell-value';
import { useFormatters } from '@/features/report-snapshots/formatters/use-formatters';
import { ChartTitle } from '@/features/report-snapshots/components/charts/chart-title';
import { normalizeCellValue } from '@/features/report-snapshots/utils/common';
import { Tooltip } from '@intercom/surge';
import {
  formatDateRangeWithoutYear,
  getComparisonDateRanges,
} from '@/features/report-snapshots/formatters/date-range-formatter';

interface CounterChartProps {
  chartSchemas: ChartSchema[];
  chartDefinition: ChartDefinition;
  dateRange?: DateRange;
  timezone?: Timezone;
}

function chartValue(
  chartSchemas: ChartSchema[],
  chartDefinition: ChartDefinition,
  formatters: ReturnType<typeof useFormatters>,
) {
  const isTimeComparisonEnabled = chartDefinition.visualizationOptions.showTimeComparison;

  const [_, currentSchema] = isTimeComparisonEnabled ? chartSchemas : [null, chartSchemas[0]];
  const columnDefinition = currentSchema.columnDefinitions[0];
  const value = currentSchema.values[0]?.[currentSchema.columnDefinitions[0].position];

  if (!value) {
    return '0';
  }

  if (
    typeof value === 'object' &&
    columnDefinition.fieldType === 'range' &&
    'from' in value &&
    'to' in value
  ) {
    return `${value.from}${value.to ? `-${value.to}` : '+'}`;
  }

  const comparisonValue = normalizeCellValue(value, columnDefinition);
  if (comparisonValue === null) {
    return '\u2014';
  }

  return formatComparisonValue(comparisonValue, columnDefinition, formatters);
}

function formatTarget(
  chartSchema: ChartSchema,
  chartDefinition: ChartDefinition,
  formatters: ReturnType<typeof useFormatters>,
) {
  const unit = chartSchema.columnDefinitions[0]?.unit;
  const isPercent = unit === 'percent';
  const isRatio = unit === 'ratio';

  if (chartDefinition.visualizationOptions.target) {
    if (isPercent) {
      return `${t`Target`}: ${chartDefinition.visualizationOptions.target.value}%`;
    }
    if (isRatio) {
      return `${t`Target`}: ${chartDefinition.visualizationOptions.target.value.toFixed(1)}`;
    }
    return `${t`Target`}: ${formatters.valueFormatters(chartDefinition.visualizationOptions.target.value, unit)}`;
  }
  return;
}

function showDataLabels(chartDefinition: ChartDefinition) {
  const series = chartDefinition.chartSeries[0];
  return series.showDataLabels;
}

function formatLabel(chartSchemas: ChartSchema[], formatters: ReturnType<typeof useFormatters>) {
  const chartSchema = chartSchemas[chartSchemas.length - 1];
  const columnDefinition = chartSchema.columnDefinitions[0];
  const value = chartSchema.values[0]?.[columnDefinition.position];
  if (typeof value === 'object' && value !== null) {
    if ('numerator' in value && 'denominator' in value) {
      return `${formatters.valueFormatters(value.numerator, 'value')} out of ${formatters.valueFormatters(value.denominator, 'value')}`;
    }
  }
  return;
}

function showComparisonValue(chartDefinition: ChartDefinition) {
  return chartDefinition.visualizationOptions.showTimeComparison;
}

interface ComparisonValueProps {
  chartSchemas: ChartSchema[];
  formatters: ReturnType<typeof useFormatters>;
  timezone?: Timezone;
  dateRange?: DateRange;
}

function ComparisonValue({ chartSchemas, formatters, timezone, dateRange }: ComparisonValueProps) {
  const [previousSchema, currentSchema] = chartSchemas;
  const { i18n } = useLingui();
  let comparisonPeriod = null;
  const comparisonDates =
    dateRange && timezone ? getComparisonDateRanges(dateRange, timezone) : null;

  if (comparisonDates && timezone) {
    const { comparisonStart, comparisonEnd } = comparisonDates;
    const comparisonDateRange: DateRange = {
      selection: 'custom',
      start: comparisonStart.toISOString(),
      end: comparisonEnd.toISOString(),
    };

    comparisonPeriod = formatDateRangeWithoutYear(comparisonDateRange, timezone, i18n);
  }

  const columnDefinition = currentSchema.columnDefinitions[0];
  const schemaComparator = createSchemaComparator(currentSchema, previousSchema);
  const comparisonResult = schemaComparator.values(0, 0);

  const comparisonDelta = comparisonResult.delta;
  const comparisonValue = formatComparisonValue(comparisonDelta ?? 0, columnDefinition, formatters);

  const comparisonContent =
    comparisonValue === '0' ? (
      <span className="flex items-center text-muted">
        <span>-</span>
      </span>
    ) : (
      <span
        className={cn(
          'flex items-center',
          comparisonResult.hasImproved ? 'text-success' : 'text-error',
        )}
      >
        {comparisonResult.hasIncreased ? <SmallUpArrowIcon /> : <SmallDownArrowIcon />}
        <span>{comparisonValue}</span>
      </span>
    );

  if (comparisonPeriod) {
    return (
      <Tooltip position="top">
        <Tooltip.Trigger>
          <div data-testid="comparison-value-trigger">{comparisonContent}</div>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <div data-testid="comparison-tooltip-content">
            <Trans>Compared to {comparisonPeriod}</Trans>
          </div>
        </Tooltip.Content>
      </Tooltip>
    );
  }

  return comparisonContent;
}

export function CounterChart(props: CounterChartProps) {
  const { chartSchemas, chartDefinition, timezone, dateRange } = props;
  const formatters = useFormatters();

  return (
    <div className="flex flex-col h-full w-full overflow-hidden rounded-xl border-neutral-border bg-base-module hover:border-neutral-border-emphasis justify-between">
      <ChartTitle chartSchema={chartSchemas[0]} chartDefinition={chartDefinition} />
      <div className="flex-col items-baseline">
        <div className="flex flex-row items-end">
          <div className="text-data-h1">
            {chartValue(chartSchemas, chartDefinition, formatters)}
          </div>
          {showComparisonValue(chartDefinition) && (
            <div className="ml-2">
              <ComparisonValue
                chartSchemas={chartSchemas}
                formatters={formatters}
                timezone={timezone}
                dateRange={dateRange}
              />
            </div>
          )}
        </div>
        <div className="flex flex-row items-center">
          {showDataLabels(chartDefinition) && (
            <div className="text-muted font-semibold">{formatLabel(chartSchemas, formatters)}</div>
          )}
        </div>
        <div className="flex flex-row items-center">
          {chartDefinition.visualizationOptions.target && (
            <div className="text-muted font-semibold">
              {formatTarget(chartSchemas[0], chartDefinition, formatters)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
